import type React from "react";

import type { z } from "zod";

import sharedConstants from "@lib/shared_constants";

import type { operatorSkillNames } from "./constants";
import type { OperatorDisabledReason } from "./operatorDisabledReasons";

export const openaiModels = Object.values(sharedConstants.BRAZE_AGENTIC_AI.MODELS.OPENAI);
export const googleModels = Object.values(sharedConstants.BRAZE_AGENTIC_AI.MODELS.GOOGLE);

/**
 * OpenAI models only offered when the sage_ai_agentic_experimental_openai_models
 * feature flipper is enabled.
 */
export const experimentalOpenaiModels = sharedConstants.BRAZE_AGENTIC_AI.EXPERIMENTAL_OPENAI_MODELS;

export type AgenticAiBaseOpenaiModel =
  (typeof sharedConstants.BRAZE_AGENTIC_AI.MODELS.OPENAI)[keyof typeof sharedConstants.BRAZE_AGENTIC_AI.MODELS.OPENAI];

/** Reasoning effort values accepted for experimental OpenAI models. */
export type OpenaiReasoningEffort =
  (typeof sharedConstants.BRAZE_AGENTIC_AI.OPENAI_REASONING_EFFORTS)[keyof typeof sharedConstants.BRAZE_AGENTIC_AI.OPENAI_REASONING_EFFORTS];

export type AgenticAiExperimentalOpenaiModel = (typeof experimentalOpenaiModels)[number];
export type AgenticAiCompositeOpenaiModel =
  | `${typeof sharedConstants.BRAZE_AGENTIC_AI.MODELS.OPENAI.GPT_5_6_TERRA}:${Extract<OpenaiReasoningEffort, "medium" | "high">}`
  | `${typeof sharedConstants.BRAZE_AGENTIC_AI.MODELS.OPENAI.GPT_6_ASTRA}:${Extract<OpenaiReasoningEffort, "low" | "medium" | "high">}`
  | `${typeof sharedConstants.BRAZE_AGENTIC_AI.MODELS.OPENAI.GPT_6_SOL}:${Extract<OpenaiReasoningEffort, "low" | "medium" | "high">}`
  | `${typeof sharedConstants.BRAZE_AGENTIC_AI.MODELS.OPENAI.GPT_6_LUNA}:${OpenaiReasoningEffort}`
  | `${typeof sharedConstants.BRAZE_AGENTIC_AI.MODELS.OPENAI.GPT_6_1_SOL}:${Extract<OpenaiReasoningEffort, "low" | "medium" | "high">}`;
export type AgenticAiOpenaiModel = AgenticAiBaseOpenaiModel | AgenticAiCompositeOpenaiModel;

export type AgenticAiGoogleModel =
  (typeof sharedConstants.BRAZE_AGENTIC_AI.MODELS.GOOGLE)[keyof typeof sharedConstants.BRAZE_AGENTIC_AI.MODELS.GOOGLE];

export type AgenticAiModel = AgenticAiOpenaiModel | AgenticAiGoogleModel;

export type AgenticAiModelProvider = "openai" | "google";

export const isGoogleModel = (model: AgenticAiModel): model is AgenticAiGoogleModel => {
  return googleModels.includes(model as AgenticAiGoogleModel);
};

export const isOpenaiModel = (model: AgenticAiModel): model is AgenticAiOpenaiModel => {
  const baseModel = model.split(":")[0] as AgenticAiBaseOpenaiModel;

  return openaiModels.includes(baseModel);
};

export const getProviderForModel = (model: AgenticAiModel): AgenticAiModelProvider => {
  if (isOpenaiModel(model)) {
    return "openai";
  }
  if (isGoogleModel(model)) {
    return "google";
  }
  throw new Error(`Unknown model: ${model}`);
};

export type AgenticAiMode =
  (typeof sharedConstants.BRAZE_AGENTIC_AI.MODES)[keyof typeof sharedConstants.BRAZE_AGENTIC_AI.MODES];

export type OperatorError =
  | "tool-not-found"
  /** No live registration carries the requested tool name. */
  | "tool-name-not-registered"
  /** The tool name is registered, but no registration carries the requested `toolEntityId`. */
  | "tool-entity-id-mismatch"
  | "unparseable-arguments"
  | "invalid-arguments-schema"
  | "parallel-limit-exceeded"
  /** The headless runtime abandoned a callback that did not settle within its time limit. */
  | "tool-timeout";

export type ToolCallState = "ready" | "running" | "complete" | "callback-error" | "operator-error" | "stopped";

/**
 * Label keys for tool call states. Both "callback-error" and "operator-error"
 * map to the "failed" label key so that existing tool label definitions
 * continue working unchanged.
 */
export type ToolCallLabelState = "ready" | "running" | "complete" | "failed" | "stopped";

export type ToolCallLabels = Partial<Record<ToolCallLabelState, string>>;

export type LabelsCallbackArgs<ToolCallArgs extends Record<string, any> = Record<string, any>> = {
  args?: ToolCallArgs;
  /** Live progress published by a long-running tool, when it has any. */
  progress?: unknown;
};

export type RenderOutputExtra<ToolCallArgs extends Record<string, any> = Record<string, any>> = {
  args?: ToolCallArgs;
  /** Provider call id of this tool call, when the renderer needs related chat state. */
  callId?: string;
};

export type RenderProgressExtra<ToolCallArgs extends Record<string, any> = Record<string, any>> =
  RenderOutputExtra<ToolCallArgs> & {
    /** Provider call id of the tool call this progress belongs to. */
    callId: string;
  };

export type RenderApprovalProps<ToolCallArgs extends Record<string, any> = Record<string, any>> = {
  args: ToolCallArgs;
  handleApprove: (customArgs?: ToolCallArgs) => void;
  handleReject: () => void;
};

export type RequiresApproval = "no" | "yes" | "always";

/** Skill names a tool can require and `registerSkill` can register. */
export type OperatorSkillName = (typeof operatorSkillNames)[number];

export type ToolConfig<
  ToolCallArgs extends Record<string, any> = Record<string, any>,
  ToolCallbackOutput = string,
> = {
  /**
   * @optional Whether the tool requires approval from the user.
   * - "no": The tool does not require approval.
   * - "yes": The tool requires approval, but can be auto-approved if the user has "Auto-approve" toggled on.
   * - "always": The tool **always** requires approval from the user, even if the user has "Auto-approve" toggled on.
   *   this is used for workspace-manipulating actions that save to the database.
   */
  requiresApproval?: RequiresApproval;
  /**
   * @optional Whether a call whose arguments fail the `parameters` schema is rejected before the
   * callback runs, with the issues returned to the model. Off by default: the failure is only logged.
   * OpenAI strict mode already guarantees the schema it accepts, so this matters for zod refinements
   * and transforms, arguments from a custom approval card, and providers that treat bounds as hints.
   */
  enforceParameterSchema?: boolean;
  /**
   * @optional Whether to use a custom approval card instead of the default one.
   * If true, the `renderApproval` function will be used to render the full
   * approval card rather than the children of the default one.
   */
  customApprovalCard?: boolean;
  /** @optional Custom render function to display in the approval request */
  renderApproval?: (props: RenderApprovalProps<ToolCallArgs>) => React.ReactNode | string;
  /** @optional Custom tool icon to display when the tool is invoked */
  toolIcon?: React.ReactNode;
  /**
   * Human-readable labels for tool call states. Required to provide clear user feedback.
   * Can be a static record or a callback that receives tool call args.
   *
   * @example
   * labels: {
   *   ready: t("weather-tool-ready", "Preparing to check weather"),
   *   running: t("weather-tool-running", "Checking weather"),
   *   complete: t("weather-tool-complete", "Checked weather"),
   *   failed: t("weather-tool-failed", "Failed to check weather"),
   *   stopped: t("weather-tool-stopped", "Stopped checking weather")
   * }
   */
  labels: ToolCallLabels | ((props: LabelsCallbackArgs<ToolCallArgs>) => ToolCallLabels);
  /**
   * @optional An abbreviated version of the arguments to the function/tool. May return a
   * Promise for summaries that resolve asynchronously (e.g. bridged iframe tools, whose
   * summary is fetched over a MessagePort) — `GenericFunctionCall` awaits it.
   *
   * Return `undefined` when the arguments cannot be summarized — for example when an id
   * resolves to no entity. The tool call then renders with no summary at all. Never
   * substitute a placeholder: an unresolved argument is not a name.
   */
  argumentsSummaryText?: (args: ToolCallArgs) => string | undefined | Promise<string | undefined>;
  /**
   * @optional Custom rendering for the tool output in the UI upon successful
   * completion of the tool call. If the output is a string, it will be rendered
   * as scrollable markdown. If the output is a React node, it will be rendered
   * as is.
   *
   * If omitted, no output is shown to the user (the output is still sent to
   * the LLM). To display the raw output as markdown, use `(output) => output`.
   */
  renderOutput?: (output: ToolCallbackOutput, extra: RenderOutputExtra<ToolCallArgs>) => React.ReactNode | string;
  /**
   * @optional Custom rendering shown while the tool is running. `progress` is whatever the
   * tool published on its status; `extra.callId` identifies this invocation.
   */
  renderProgress?: (progress: unknown, extra: RenderProgressExtra<ToolCallArgs>) => React.ReactNode;
  /**
   * @optional Custom rendering for the tool output in the UI when the tool
   * callback throws an error. If omitted, no error output is shown to the
   * user. To display the error message as markdown, use
   * `(error) => error.message`.
   */
  renderCallbackError?: (error: Error, extra: RenderOutputExtra<ToolCallArgs>) => React.ReactNode | string;
  /**
   * @optional Whether to force the output to be expanded in the UI.
   * If true, the output will be expanded in the UI even if the user has not interacted with it.
   * In this case, we also don't indent the output in the UI.
   */
  forceExpanded?: boolean;
  /** @optional Whether to indent the output in the UI. If unset, defaults to true. */
  indentOutput?: boolean;
  /**
   * @optional When true, Operator augments the LLM-visible schema with optional
   * `operatorTraversalPath` and `operatorTraversalDepth` parameters and
   * post-processes tool output:
   * - If `operatorTraversalPath` is provided, resolves the path and returns only that slice.
   * - If `operatorTraversalDepth` is provided, trims the tree to that depth.
   */
  allowJsonPathTraversal?: boolean;
  /**
   * @optional Skill that must be loaded before this tool is sent to the model, while
   * `sage_ai_agentic_prompt_cache_skills` is on. Omit it and the tool is sent on every
   * request. Pass the skill name literal to hold the tool until that skill is loaded.
   */
  requiredSkill?: OperatorSkillName;
  /**
   * @optional When true, the tool's effect lands somewhere only a person at the browser can
   * observe, such as the clipboard. The headless runtime has nobody there, so the tool is not
   * registered at all: a call could return nothing and would only spend a turn.
   */
  humanOnly?: boolean;
  /** @optional Headless time limit for this tool's callback, for tools that legitimately run longer than `headlessToolCallTimeoutMs`. */
  headlessTimeoutMs?: number;
  /**
   * @optional When true, this tool stays in the cached OpenAI tools prefix (`defer_loading: false`)
   * and is not placed in a namespace. Leave unset to defer it into `page_controls`.
   */
  includeInPromptCache?: boolean;
  /** FullStory reporting group for this tool. */
  toolGroup: FrontendToolGroup;
};

export type FrontendToolGroup =
  | "Agentic Creation & Editing"
  | "Analysis"
  | "Braze MCP"
  | "Creating Support Ticket"
  | "Docs Search & Lookup"
  | "Other"
  | "Page Search"
  | "Third-party Integration"
  | "Workspace Search & Lookup";

export type FrontendToolOptions<
  ToolCallArgs extends Record<string, any> = Record<string, any>,
  ToolCallbackOutput = string,
> = ToolConfig<ToolCallArgs, ToolCallbackOutput>;

export type ToolDefinition<ToolCallArgs = any> = {
  /**
   * Optional instance id for disambiguating multiple registrations that share the same
   * tool name. Injected into the LLM-visible schema as `toolEntityId` when set.
   */
  toolEntityId?: string;
  /** Function name, provided to the LLM */
  name: string;
  /**
   * Function description, provided to the LLM.
   * This should contain sufficient information for the LLM to be able to determine
   * when to call the tool, how to choose parameters, and example output it will produce.
   * See https://platform.openai.com/docs/guides/function-calling#best-practices-for-defining-functions
   */
  description: string;
  /** Function parameters, provided to the LLM.  */
  parameters: z.Schema<ToolCallArgs>;
  /**
   * A JSON Schema sent in place of `parameters`, with strict mode off: for a third-party schema strict mode cannot
   * express. `parameters` then only has to accept what the model sends.
   */
  nonStrictParameters?: Record<string, unknown>;
};

export interface ToolSchemaOptions {
  allowJsonPathTraversal?: boolean;
  /** Registered entity ids for this tool name; when set, injected as a `toolEntityId` enum. */
  toolEntityIds?: string[];
}

export type ToolCallbackContext = {
  sessionId: string;
  userMessageId: string;
  requestId: string;
  /**
   * Assistant message that issued this call. `useToolCaller` always sets this.
   * Optional on the type so existing tool doubles that ignore the context stay valid.
   */
  messageId?: string;
  /**
   * Provider call id for this invocation. `useToolCaller` always sets this.
   * Optional on the type so existing tool doubles that ignore the context stay valid.
   */
  callId?: string;
  /**
   * Set when the assistant message belongs to a delegated sub-agent.
   * `callId` is the parent `delegate_task` call. Absent for orchestrator calls.
   */
  delegatedFrom?: {
    messageId: string;
    callId: string;
  };
};

export type FrontendTool<
  ToolCallArgs extends Record<string, any> = Record<string, any>,
  ToolCallbackOutput = string,
> = {
  /** Tool definition, passed to the LLM */
  definition: ToolDefinition<ToolCallArgs>;
  /** The function to call when the tool is used */
  callback: (args: ToolCallArgs, context: ToolCallbackContext) => ToolCallbackOutput | Promise<ToolCallbackOutput>;
  /** Frontend tool options */
  options: FrontendToolOptions<ToolCallArgs, ToolCallbackOutput>;
  /**
   * Which of the dashboard's two React trees registered this tool: `true` for the SPA
   * under `#react-root`, `false` for a Knockout-embedded island outside it. Both share
   * this one store.
   *
   * Set only for registrations governed by `isCorrectReactAppRootState`, because that
   * gate is what stops both trees from holding the same tool at once - so only for those
   * is a cross-tree collision the route handoff rather than a real duplicate. Left
   * undefined for `ignoreReactAppRootState` registrations, which are live in both trees
   * by design, and for transports that bypass `AgenticAiContextProvider`.
   */
  originIsWithinReactRoot?: boolean;
  /**
   * Whether this tool was registered with {@link RegisterToolParams.ignoreReactAppRootState},
   * i.e. it is a global tool that is live in both React trees.
   */
  ignoreReactAppRootState?: boolean;
};

/** Convenience alias for a FrontendTool with erased generics, used in heterogeneous collections. */
export type AnyFrontendTool = FrontendTool<any, any>;

export type SkillDefinition = {
  /** Unique identifier for the skill, used as the argument to load_skill. */
  name: OperatorSkillName;
  /** Localized label shown in the Operator UI when the skill is loaded. */
  label: string;
  /** Short description teaching the LLM when to use this skill (sent as part of load_skill). */
  description: string;
  /** Full instructions given to the LLM when the skill is loaded via load_skill. */
  instructions: string;
};

export type RegisterSkillParams = SkillDefinition & {
  /**
   * @deprecated Whether this should be registered regardless of the current
   * React app root state. Do not use this parameter unless you are registering
   * from outside the React app root (e.g. in AgenticChatInterface, which is
   * rendered directly in dashboard.html.erb).
   */
  ignoreReactAppRootState?: boolean;
};

export type AgenticAiRegisterSkill = (params: RegisterSkillParams) => () => void;

export type AgenticAiDeveloperContextOptions = {
  /**
   * @deprecated Whether this should be registered regardless of the current
   * React app root state. Do not use this parameter unless you are registering
   * from outside the React app root (e.g. in AgenticChatInterface, which is
   * rendered directly in dashboard.html.erb).
   */
  ignoreReactAppRootState?: boolean;
  /**
   * When true, registering a context with an already-registered identifier
   * won't log an error.
   */
  ignoreDuplicates?: boolean;
  /**
   * When true, this context is included on requests that carry a new user
   * prompt and skipped for agent-loop follow-up requests (e.g. tool call
   * outputs). Use for volatile context (like the current page scrape) that
   * should accompany each user message but not bloat every agent turn.
   */
  onUserPromptOnly?: boolean;
  /**
   * When true, and `sage_ai_agentic_prompt_cache` is on, this context is part of
   * the cached prompt prefix. Leave unset for context that changes between turns.
   * The backend sorts cacheable context by text and places the cache breakpoint
   * after the last one.
   */
  includeInPromptCache?: boolean;
};

export type AgenticAiDeveloperContextValue = string | (() => string);

export type AgenticAiDeveloperContext = {
  identifier: string;
  value: AgenticAiDeveloperContextValue;
  options: AgenticAiDeveloperContextOptions;
};

export type AgenticAiLiquidField = {
  /** Registry key: the field's DOM id, which Braze* inputs also use as the toolEntityId of their update tool */
  id: string;
  /** Human-readable label for the field we're checking for Liquid errors */
  label: string;
  /** Function to get the current value of the field */
  getValue: () => string;
};

export type AgenticAiRegisterLiquidField = (key: string, label: string, getValue: () => string) => void;

export type AgenticAiState = {
  /**
   * additional context sent to the LLM alongside the user prompt;
   *  provided as developer messages, this can supply information about
   *  the current page (such as its content) to the LLM.
   */
  context: Record<string, AgenticAiDeveloperContext>;
  /** the list of identifiers for the contexts (map to keys) */
  contextIdentifiers: Record<string, string[]>;
  /** the session ID for the current chat session */
  sessionId: string;
  /** the list of frontend tools that are available to the agent */
  frontendTools: Record<string, AnyFrontendTool>;
  /** the list of frontend tool names (map to keys) */
  frontendToolNames: Record<string, string[]>;
  /** indicates whether the chat UI is open */
  isOpen: boolean;
  /** registered liquid fields for syntax checking */
  liquidFields: Record<string, AgenticAiLiquidField>;
  /** indicates whether the chat was opened from the support menu entry point */
  supportEntryPoint: boolean;
  /**
   * Markdown response to show after a new-topic divider when an Operator entry-point
   * button opens into an existing idle/error chat. Cleared on close and once handled.
   */
  pendingExistingChatResponse?: string;
  /**
   * When set (e.g. from an Operator entry-point button), included in dynamic-prompts API
   * requests so suggestions can be tailored. Cleared on close and on each open unless
   * {@link OpenChatOptions.dynamicPromptsContext} is provided.
   *
   * Prompt text, not an identifier - see {@link OpenChatOptions.dynamicPromptsContext}.
   */
  dynamicPromptsContext?: string;
  /**
   * Brand guideline IDs awaiting attachment as user-context chips by the chat UI
   * (see useSelectedUserContexts). While non-empty, pending prompt dispatch is held
   * so the attached context makes it into the first request. Cleared once consumed
   * and on close.
   */
  pendingBrandGuidelineContextIds?: string[];
  /**
   * Registered attached files, keyed by uuid (same pattern as `context`). Read
   * fresh at send time; the files ride requests as the generic `attached_files`
   * parameter. Registering features keep their descriptors' URLs
   * fresh, so Operator never mints or fetches anything here.
   */
  attachedFiles: Record<string, OperatorAttachedFileRegistration>;
  /**
   * Maps an attached-files registration identifier to its uuid keys, for
   * duplicate detection (same pattern as `contextIdentifiers`).
   */
  attachedFileIdentifiers: Record<string, string[]>;
  /** registered skills, keyed by uuid (same pattern as frontendTools) */
  skillDefinitions: Record<string, SkillDefinition>;
  /** the list of registered skills (map to keys, same pattern as frontendToolNames) */
  registeredSkillNames: Record<string, string[]>;
  /** names of skills the parent loaded this session. A sub-agent load stays off this list. */
  loadedSkillNames: string[];
  /**
   * Skills a delegated call loaded, keyed by that call's id.
   * They unlock tools on the child request only.
   */
  loadedSkillNamesByDelegateCallId: Record<string, string[]>;
};

/**
 * A generic Operator file attachment: a small descriptor whose presigned URL
 * the backend streamer downloads worker-side and includes as LLM context
 * (the camelCase mirror of the backend's `attached_files` entries).
 * The first source is the persisted campaign brief, but any feature able to
 * mint a presigned URL for its file can attach one the same way.
 */
export type OperatorAttachedFile = {
  /** Presigned, time-limited download URL to the private upload bucket. */
  url: string;
  /** Original file name, e.g. "brief.pdf". */
  fileName: string;
  /** Extension-derived MIME type, e.g. "application/pdf". */
  mimeType: string;
  /** Reference note rendered after the file's content in the LLM turn. */
  context: string;
  /**
   * Epoch seconds at which {@link url} expires. The registering feature keeps
   * the descriptor fresh (e.g. refetching on an interval derived from this), so
   * Operator sends whatever it currently holds without re-minting at send time.
   */
  expiresAt: number;
  /**
   * Epoch seconds of the file's last (re)upload -- content identity for
   * change detection, so a same-named replacement re-attaches while presign
   * re-mints of the same file dedupe. Frontend-only (never sent on the wire,
   * like expiresAt); optional because older backends omit it.
   */
  fileUpdatedAt?: number;
};

/**
 * A feature's attached-file registration: the files plus the identifier used to
 * detect duplicate registrations (mirrors {@link AgenticAiDeveloperContext}).
 */
export type OperatorAttachedFileRegistration = {
  identifier: string;
  files: OperatorAttachedFile[];
};

export type RegisterAttachedFilesOptions = {
  /**
   * @deprecated Escape hatch matching
   * {@link RegisterToolParams.ignoreReactAppRootState}. Use only when a global
   * Operator tool registered outside the React app root later registers files
   * from its callback; page-scoped registrations should respect the active root.
   */
  ignoreReactAppRootState?: boolean;
};

export type AgenticAiRegisterAttachedFiles = (
  identifier: string,
  files: OperatorAttachedFile[],
  options?: RegisterAttachedFilesOptions
) => () => void;

export type AgenticAiRegisterContext = (
  identifier: string,
  value: AgenticAiDeveloperContextValue,
  options?: AgenticAiDeveloperContextOptions
) => () => void;

export type RegisterToolParams<
  ToolCallArgs extends Record<string, any> = Record<string, any>,
  ToolCallbackOutput = string,
> = {
  /**
   * Optional instance id stored on the tool definition for lookup when multiple
   * components register the same tool name (e.g. `click_button`).
   */
  toolEntityId?: string;
  name: string;
  description: string;
  parameters: z.Schema<ToolCallArgs>;
  /**
   * A JSON Schema sent in place of `parameters`, with strict mode off: for a third-party schema strict mode cannot
   * express. `parameters` then only has to accept what the model sends.
   */
  nonStrictParameters?: Record<string, unknown>;
  /**
   * Runs when the agent calls this tool.
   *
   * The return value is detached with {@link detachToolOutput} before it is stored, so returning
   * live application state is safe and callbacks must not copy defensively. Copying here as well
   * only pays for a second deep clone, and implies a per-tool responsibility that does not exist.
   *
   * This matters because the chat reducer stores tool output inside Immer, which deep-freezes
   * every draftable value it stores. Before the copy existed, a tool returning live state froze
   * that state and the next in-place edit threw "Cannot assign to read only property".
   */
  callback: (args: ToolCallArgs, context: ToolCallbackContext) => ToolCallbackOutput | Promise<ToolCallbackOutput>;
  options: FrontendToolOptions<ToolCallArgs, ToolCallbackOutput>;
  /**
   * @deprecated Whether this should be registered regardless of the current
   * React app root state. Do not use this parameter unless you are registering
   * from outside the React app root (e.g. in AgenticChatInterface, which is
   * rendered directly in dashboard.html.erb).
   */
  ignoreReactAppRootState?: boolean;
};

export type AgenticAiRegisterTool = <ToolCallArgs extends Record<string, any>, ToolCallbackOutput = string>(
  params: RegisterToolParams<ToolCallArgs, ToolCallbackOutput>
) => () => void;

export type GetFrontendTool = (toolName: string, toolEntityId?: string) => AnyFrontendTool | undefined;

export type GetToolEntityIds = (toolName: string, delegateCallId?: string) => string[];

export type OpenChatOptions = {
  /** The log action to use when opening the chat. */
  logAction?: "open" | "automatic open" | "headless open";
  /** Whether to open the chat with the support entry point active. */
  withSupportEntryPoint?: boolean;
  /**
   * Optional extra context for dynamic prompt generation. When omitted, any prior
   * context is cleared so a later open does not reuse it.
   *
   * This is **prompt text, not an identifier**: the value is spliced verbatim into the prompt
   * sent to the model that writes the suggestion chips, so pass a sentence describing what the
   * user just did and what the suggestions should cover. A slug gives the model nothing to work
   * with and risks leaking an internal-looking identifier into a chip.
   */
  dynamicPromptsContext?: string;
  /**
   * Brand guideline IDs to attach as user-context chips when the chat opens
   * (same chips as manual selection via the AddContextButton). Unioned into any
   * IDs still pending; when omitted, pending IDs are left untouched.
   */
  brandGuidelineContextIds?: string[];
  /**
   * Markdown shown after a new-topic divider when opening into an existing idle/error
   * chat from an Operator entry-point button. Empty chats use dynamic prompts when
   * {@link dynamicPromptsContext} is set; otherwise this response is shown.
   */
  existingChatResponse?: string;
};

/**
 * Options for {@link AgenticAiContextActions.getAllFrontendTools}.
 */
export interface GetAllFrontendToolsOptions {
  /**
   * Include tools whose required skill is not loaded yet.
   * Dynamic prompt suggestions set this. That job runs when chat opens, before
   * any skill is loaded, and its prompt never joins the cached model request.
   */
  includeToolsHeldForSkills?: boolean;
  /**
   * Delegated call whose loaded skills should also unlock tools.
   * Omit on a parent request. A skill the sub-agent loaded stays off that request.
   */
  delegateCallId?: string;
}

export type AgenticAiContextActions = {
  /** The function to open the chat UI. */
  openChat: (options?: OpenChatOptions) => void;
  /**
   * Queue brand guideline IDs to attach as user-context chips, as
   * {@link OpenChatOptions.brandGuidelineContextIds} does, without opening the
   * chat: for a chat that is already open, such as from a tool call. Unioned
   * into any IDs still pending; the rest of the chat state is left alone.
   * Does nothing while the chat is closed.
   */
  attachBrandGuidelineContexts: (brandGuidelineContextIds: readonly string[]) => void;
  /** The function to close the chat UI. */
  closeChat: () => void;
  /** The function to toggle the chat UI. */
  toggleOpenChat: () => void;
  /** Generate and set a new session ID for the Operator session. */
  newSession: () => void;
  /** Send a message to the Operator. */
  sendUserPrompt: (message: string) => void;
  /**
   * Register a context with the agentic AI store.
   *
   * @param identifier A unique identifier to register the context with. This is
   * used to avoid duplicate contexts.
   * @param value The value of the context. Can be a function or a string. If a
   * function, it will be called when the request is sent to the LLM.
   * @param options (optional) Options for the context.
   *
   * @returns a cleanup function to unregister the context
   */
  registerContext: AgenticAiRegisterContext;
  /**
   * Register a feature's Operator file attachments. Sync data +
   * cleanup, mirroring {@link registerContext}: the files are stored as-is and
   * read fresh at send time. The registering feature owns keeping the
   * descriptors' presigned URLs unexpired.
   *
   * @param identifier A unique identifier for this registration, used to detect
   * duplicates.
   * @param files The feature's current attached files.
   * @returns a cleanup function to unregister the files
   */
  registerAttachedFiles: AgenticAiRegisterAttachedFiles;
  /**
   * Read every registered attached file, fresh from the Redux store (mirrors
   * {@link getAllFrontendTools}). Cannot fail -- returns [] when nothing is
   * registered.
   *
   * @returns the combined attached files across registrations
   */
  getAttachedFiles: () => OperatorAttachedFile[];
  /**
   * Register a tool with the agentic AI store.
   *
   * @param name The name of the tool, provided to the LLM.
   * @param description The description of the tool, provided to the LLM.
   *  This should contain sufficient information for the LLM to be able to determine
   *  when to call the tool, how to choose parameters, and example output it will produce.
   *  See https://platform.openai.com/docs/guides/function-calling#best-practices-for-defining-functions
   * @param parameters The parameters of the tool, provided to the LLM.
   * @param callback The callback to call when the tool is used.
   * @param options (optional) Options for the tool (not given to the LLM, but used in the Operator UI).
   * @param definitionOptions (optional) Options for the tool definition, provided to the LLM..
   *
   * @returns a cleanup function to unregister the tool
   */
  registerTool: AgenticAiRegisterTool;
  /**
   * Register a skill with the agentic AI store. Skills are "how-to" packages
   * that the LLM can load on demand via the load_skill tool.
   *
   * @returns a cleanup function to unregister the skill
   */
  registerSkill: AgenticAiRegisterSkill;
  /**
   * Mark a registered skill as loaded, making its gated tools available.
   *
   * @param name skill name
   * @param delegateCallId when set, the skill unlocks tools only for that delegated call
   */
  loadSkill: (name: string, delegateCallId?: string) => void;
  /**
   * Get a tool by name, optionally disambiguated by `toolEntityId`.
   *
   * @param toolName The name of the tool.
   * @param toolEntityId When set, matches `tool.definition.toolEntityId` (e.g. from tool call args).
   *
   * @returns the tool or undefined if no matching registration exists
   */
  getFrontendTool: GetFrontendTool;
  /**
   * Get all currently registered frontend tools, reading fresh from the Redux store.
   * This ensures newly registered tools are included even if called mid-render.
   *
   * Tools whose skill is not loaded are omitted unless `includeToolsHeldForSkills`
   * is set. Dynamic prompt suggestions set it: that job runs at chat open, before
   * any skill is loaded, and it never joins the cached model request.
   *
   * @returns array of frontend tools the caller should see
   */
  getAllFrontendTools: (options?: GetAllFrontendToolsOptions) => AnyFrontendTool[];
  /**
   * Get every registered developer context, reading fresh from the Redux store
   * (mirrors {@link getAllFrontendTools}), so a context registered in the same
   * commit that queued a prompt rides that prompt's request.
   *
   * @returns the registered contexts, keyed by registration
   */
  getAllContexts: () => Record<string, AgenticAiDeveloperContext>;
  /**
   * Get all registered `toolEntityId` values for a tool name, reading fresh from the Redux store.
   */
  getToolEntityIds: GetToolEntityIds;
  /**
   * Register a liquid field for syntax checking. Uses upsert semantics -
   * if a field with the same key already exists, it will be updated.
   *
   * @param key Stable unique identifier for the field (e.g., component ID)
   * @param label Human-readable label for error reporting
   * @param getValue Function to get the current value of the field
   */
  registerLiquidField: AgenticAiRegisterLiquidField;
  /**
   * Unregister a specific liquid field by key.
   *
   * @param key The key of the field to unregister
   */
  unregisterLiquidField: (key: string) => void;
  /**
   * Get all currently registered liquid fields, reading fresh from the Redux store.
   *
   * @returns array of all registered liquid fields
   */
  getAllLiquidFields: () => AgenticAiLiquidField[];
  /** Clear the support entry point flag after it has been handled. */
  clearSupportEntryPoint: () => void;
  /** Clear a pending existing-chat entry-point response after it has been handled. */
  clearPendingExistingChatResponse: () => void;
  /**
   * Clear pending brand guideline context IDs once the chat UI has consumed them.
   * Pass the specific IDs that were consumed so any IDs queued by a concurrent
   * `openChat` survive; omit the argument to clear the entire pending set.
   */
  clearPendingBrandGuidelineContexts: (consumedIds?: string[]) => void;
};

/**
 * Operator actions safe for dashboard consumers. These callbacks do not close
 * over Redux state, so registry and chat updates do not change their identity.
 */
export type AgenticAiContextType = Pick<
  AgenticAiContextActions,
  | "openChat"
  | "attachBrandGuidelineContexts"
  | "closeChat"
  | "toggleOpenChat"
  | "newSession"
  | "sendUserPrompt"
  | "registerContext"
  | "registerAttachedFiles"
  | "registerTool"
  | "registerSkill"
  | "registerLiquidField"
  | "unregisterLiquidField"
>;

/**
 * Operator-owned accessors and commands for the global Agentic AI Redux store.
 * Dashboard features should use {@link AgenticAiContextType} instead.
 */
export type AgenticAiRuntimeContextType = Pick<
  AgenticAiContextActions,
  | "getAttachedFiles"
  | "loadSkill"
  | "getFrontendTool"
  | "getAllFrontendTools"
  | "getAllContexts"
  | "getToolEntityIds"
  | "getAllLiquidFields"
  | "clearSupportEntryPoint"
  | "clearPendingExistingChatResponse"
  | "clearPendingBrandGuidelineContexts"
>;

export type AgenticAiOpenChatAction = {
  type: "agenticAi/openChat";
  withSupportEntryPoint?: boolean;
  dynamicPromptsContext?: string;
  brandGuidelineContextIds?: string[];
  existingChatResponse?: string;
};

export type AgenticAiAttachBrandGuidelineContextsAction = {
  type: "agenticAi/attachBrandGuidelineContexts";
  brandGuidelineContextIds: readonly string[];
};

export type AgenticAiCloseChatAction = {
  type: "agenticAi/closeChat";
};

export type AgenticAiRegisterToolAction = {
  type: "agenticAi/registerTool";
  /** Used to unregister. */
  key: string;
  tool: AnyFrontendTool;
};

export type AgenticAiUnregisterToolAction = {
  type: "agenticAi/unregisterTool";
  key: string;
};

export type AgenticAiRegisterContextAction = AgenticAiDeveloperContext & {
  type: "agenticAi/registerContext";
  /** Used to unregister. */
  key: string;
};

export type AgenticAiUnregisterContextAction = {
  type: "agenticAi/unregisterContext";
  key: string;
};

export type AgenticAiSetSessionIdAction = {
  type: "agenticAi/setSessionId";
  sessionId: string;
};

export type AgenticAiRegisterLiquidFieldAction = {
  type: "agenticAi/registerLiquidField";
  /** Used to unregister. */
  key: string;
  field: AgenticAiLiquidField;
};

export type AgenticAiUnregisterLiquidFieldAction = {
  type: "agenticAi/unregisterLiquidField";
  key: string;
};

export type AgenticAiClearSupportEntryPointAction = {
  type: "agenticAi/clearSupportEntryPoint";
};

export type AgenticAiClearPendingExistingChatResponseAction = {
  type: "agenticAi/clearPendingExistingChatResponse";
};

export type AgenticAiClearPendingBrandGuidelineContextsAction = {
  type: "agenticAi/clearPendingBrandGuidelineContexts";
  /** Specific IDs that were consumed; omit to clear the entire pending set. */
  consumedIds?: string[];
};

export type AgenticAiRegisterAttachedFilesAction = {
  type: "agenticAi/registerAttachedFiles";
  /** UUID key used to unregister. */
  key: string;
  identifier: string;
  files: OperatorAttachedFile[];
};

export type AgenticAiUnregisterAttachedFilesAction = {
  type: "agenticAi/unregisterAttachedFiles";
  key: string;
};

export type AgenticAiRegisterSkillAction = {
  type: "agenticAi/registerSkill";
  /** UUID key used to unregister. */
  key: string;
  skill: SkillDefinition;
};

export type AgenticAiUnregisterSkillAction = {
  type: "agenticAi/unregisterSkill";
  key: string;
};

export type AgenticAiLoadSkillAction = {
  type: "agenticAi/loadSkill";
  name: string;
  /** Parent delegate call id when a sub-agent loaded the skill. Absent for the parent. */
  delegateCallId?: string;
};

export type AgenticAiAction =
  | AgenticAiOpenChatAction
  | AgenticAiAttachBrandGuidelineContextsAction
  | AgenticAiCloseChatAction
  | AgenticAiSetSessionIdAction
  | AgenticAiRegisterToolAction
  | AgenticAiUnregisterToolAction
  | AgenticAiRegisterContextAction
  | AgenticAiUnregisterContextAction
  | AgenticAiRegisterLiquidFieldAction
  | AgenticAiUnregisterLiquidFieldAction
  | AgenticAiClearSupportEntryPointAction
  | AgenticAiClearPendingExistingChatResponseAction
  | AgenticAiClearPendingBrandGuidelineContextsAction
  | AgenticAiRegisterAttachedFilesAction
  | AgenticAiUnregisterAttachedFilesAction
  | AgenticAiRegisterSkillAction
  | AgenticAiUnregisterSkillAction
  | AgenticAiLoadSkillAction;

export interface OperatorDisabledProps {
  /**
   * Developer-facing rationale for intentionally withholding this control's mutation tool from
   * Operator. The control remains visible to people and readable in page descriptions.
   */
  operatorDisabledReason?: OperatorDisabledReason;
}

export interface BaseOperatorProps extends OperatorDisabledProps {
  /**
   * The label for the component, shown to the user in tool call approval messages
   * and used by the Operator to identify the component in the DOM. For more information,
   * @see [README](../agenticAi/README.md)
   */
  operatorLabel: string;
}

export type OperatorRequiresApproval = Exclude<RequiresApproval, "no">;

export interface OperatorRequiresApprovalProps {
  /**
   * Override the default approval behavior for Operator tool calls on this component.
   * - "yes": Requires approval, but can be auto-approved if auto-approve is enabled (default)
   * - "always": Always requires manual approval, even with auto-approve enabled.
   *   Use this for workspace-manipulating actions that save to the database.
   */
  operatorRequiresApproval?: OperatorRequiresApproval;
}

export interface OperatorSelectProps extends BaseOperatorProps {
  /**
   * A function to format the option as a string. This is used by Operator
   * to be able to read the option value and display it to the user in the approval card.
   * If not provided, defaults to extracting `option.label` (falling back to empty string).
   * @see [README](../agenticAi/README.md)
   */
  formatOptionStringLabel?: (option: any) => string;
}

/** A search/replace edit that Operator tools apply to a document body. */
export interface SearchReplaceDiff {
  search: string;
  replace: string;
}
